/**
 * @file components/shared/Navbar.jsx
 * @description Fixed navbar with "Scroll to Top" logo functionality.
 */

import { useEffect, useRef, useState } from "react";
import { logoutUser } from "../../services/firebase";
import { useAuth } from "../../utils/AuthContext";
import { useSubscription } from "../../hooks/useSubscription";
import { User, LogOut, ChevronDown, Zap, Heart, Crown, HelpCircle, Sparkles, Download } from "lucide-react";
import { STREAK_KEY } from "../../hooks/useHabitStreak";
import { HelpCenterModal } from "./HelpCenterModal";
import { UpgradeModal } from "./UpgradeModal";
import { useMeals } from "../../hooks/useMeals";
import { useWeights } from "../../hooks/useWeight";
import { exportAllData } from "../../services/exportData";

export default function Navbar({ onShowAuth }) {
  const { user, uid } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [showHelpCenter, setShowHelpCenter] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState("default");
  const dropdownRef = useRef(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const { isPremium, customerPortalUrl } = useSubscription(uid);
  const { data: meals = [] } = useMeals(uid);
  const { data: weightLogs = [] } = useWeights(uid);

  const { habits: habitStreak } =
    JSON.parse(localStorage.getItem(STREAK_KEY)) || "0";

  function handleExport() {
    setShowDropdown(false);
    if (!isPremium) {
      setUpgradeReason("export");
      setShowUpgrade(true);
      return;
    }
    exportAllData(meals, weightLogs);
  }

  // Handle scroll detection for shadow/transparency
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Smooth Scroll to Top Function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <div style={{ height: "calc(3.5rem + env(safe-area-inset-top))" }} />

      <nav
        className={`
          fixed top-0 left-0 right-0 z-[100] w-full transition-all duration-300
          ${
            isScrolled
              ? "bg-[#0A0A0A]/95 backdrop-blur-md shadow-[0_8px_30px_rgb(0,0,0,0.8)]"
              : "bg-[#0A0A0A]"
          }
          border-b border-white/10
        `}
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-5xl mx-auto px-4 flex items-center justify-between h-14 relative">
          {/* Logo - Now with Click-to-Top */}
          <div
            onClick={scrollToTop}
            className="z-10 cursor-pointer select-none active:scale-95 transition-transform"
          >
            <span className="text-lg font-bold tracking-tight text-white">
              Diet<span className="text-brand-green">Reg</span>
            </span>
          </div>

          {/* Centered Habit Streak */}
          {user && parseInt(habitStreak) > 0 && (
            <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-1 bg-brand-green/10 border border-brand-green/20 rounded-full">
              <Zap
                size={10}
                className="text-brand-green fill-brand-green animate-pulse"
              />
              <span className="text-[9px] font-black text-brand-green tracking-[0.1em] uppercase">
                {habitStreak} Day Streak
              </span>
            </div>
          )}

          {/* Right Side Actions */}
          <div className="flex items-center gap-4 z-10">
            {user ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="flex items-center gap-2 p-1 rounded-lg transition-colors active:bg-white/10"
                >
                  {user.photoURL && !avatarError ? (
                    <img
                      src={user.photoURL}
                      alt="profile"
                      className="w-7 h-7 rounded-full border border-white/20 object-cover"
                      onError={() => setAvatarError(true)}
                    />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-brand-green/20 border border-brand-green/30 flex items-center justify-center text-brand-green font-bold text-xs">
                      {(user.displayName || user.email || "U").charAt(0).toUpperCase()}
                    </div>
                  )}

                  <ChevronDown
                    size={12}
                    className={`text-gray-500 transition-transform duration-200 ${showDropdown ? "rotate-180" : ""}`}
                  />
                </button>

                {showDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-[#121212] border border-white/10 rounded-xl shadow-2xl py-1 animate-in fade-in zoom-in duration-150 overflow-hidden">
                    <div className="px-4 py-3 border-b border-white/5 bg-white/[0.02]">
                      <p className="text-white font-medium truncate text-xs">
                        {user.displayName || "User"}
                      </p>
                      <p className="text-[9px] text-brand-green font-bold uppercase tracking-widest mt-0.5">
                        {habitStreak} Day Consistency
                      </p>
                    </div>

                    {!isPremium && (
                      <button
                        onClick={() => {
                          setShowDropdown(false);
                          setShowUpgrade(true);
                        }}
                        className="w-full text-left px-4 py-3 text-brand-amber hover:text-brand-green hover:bg-brand-green/10 transition-colors flex items-center gap-3 font-semibold uppercase tracking-wider text-[10px] border-b border-white/5 bg-gradient-to-r from-brand-amber/5 to-transparent"
                      >
                        <Sparkles size={14} className="fill-current" />
                        Go Premium
                      </button>
                    )}

                    <button
                      onClick={() => logoutUser()}
                      className="w-full text-left px-4 py-3 text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-3 font-semibold uppercase tracking-wider text-[10px]"
                    >
                      <LogOut size={14} />
                      Logout
                    </button>

                    {isPremium && (
                      <a
                        href={customerPortalUrl || "https://app.lemonsqueezy.com/my-orders"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full text-left px-4 py-3 text-gray-400 hover:text-brand-green hover:bg-brand-green/10 transition-colors flex items-center gap-3 font-semibold uppercase tracking-wider text-[10px] border-t border-white/5"
                      >
                        <Crown size={14} />
                        Manage Subscription
                      </a>
                    )}

                    <button
                      onClick={handleExport}
                      className="w-full text-left px-4 py-3 text-gray-400 hover:text-purple-400 hover:bg-purple-500/10 transition-colors flex items-center gap-3 font-semibold uppercase tracking-wider text-[10px] border-t border-white/5"
                    >
                      <Download size={14} />
                      Export Data
                      {!isPremium && <Crown size={10} className="text-brand-amber ml-auto" />}
                    </button>

                    <button
                      onClick={() => {
                        setShowDropdown(false);
                        setShowHelpCenter(true);
                      }}
                      className="w-full text-left px-4 py-3 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 transition-colors flex items-center gap-3 font-semibold uppercase tracking-wider text-[10px] border-t border-white/5"
                    >
                      <HelpCircle size={14} />
                      Help & Support
                    </button>

                    <a
                      href="https://buymeacoffee.com/dietreg"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full text-left px-4 py-3 text-gray-400 hover:text-brand-amber hover:bg-brand-amber/10 transition-colors flex items-center gap-3 font-semibold uppercase tracking-wider text-[10px] border-t border-white/5"
                    >
                      <Heart size={14} className="fill-current" />
                      Buy me a coffee ☕
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <div className="px-3 py-1 bg-white/5 rounded border border-white/10">
                <span className="text-gray-500 text-[10px] uppercase tracking-widest font-bold">
                  Guest
                </span>
              </div>
            )}
          </div>
        </div>
      </nav>

      {showHelpCenter && (
        <HelpCenterModal onClose={() => setShowHelpCenter(false)} />
      )}

      {showUpgrade && (
        <UpgradeModal onClose={() => setShowUpgrade(false)} reason={upgradeReason} onShowAuth={onShowAuth} />
      )}
    </>
  );
}
